import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import { ProgressBar } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import {
  LogoIcon,
  ArrowLeftGreyIcon,
  ArrowRightGreyIcon,
} from "../../../assets";
import {
  authConstants,
  formsNamesEnums,
  ELIGIBILITY_OPTIONS,
  REGISTER_SURVEY_FORMS,
} from "../../../constants";
import { ERROR_TOAST } from "../../../constants/toast.constants";
import { getRegisterSurveySchema } from "../../../schemas";
import { customToast } from "../../../shared/utility";
import {
  authActions,
  homeActions,
  countryAction,
  propertyActions,
} from "../../../redux/actions";
import {
  AgeForm,
  OwnHome,
  DayToDay,
  NameForm,
  HealthCare,
  BudgetRange,
  FamilyMembers,
  TaxPreference,
  ThankYouScreen,
  VisaPreference,
  CurrentCountry,
  CreatePassword,
  HowManyProperties,
  CountryPreference,
  PassportCountries,
  HobbiesAndInterests,
  PropetyTypePreference,
  CitizenshipPreference,
  ConfirmEligibleCountries,
  LovedOnesDistancePreference,
  HolidayDestinationPreference,
} from "./forms";

const {
  BUDGET,
  HOBBIES,
  AGE_FORM,
  PASSWORD,
  OWN_HOME,
  NAME_FORM,
  THANK_YOU,
  LOVED_ONES,
  DAY_TO_DAY,
  HEALTH_CARE,
  FAMILY_MEMBERS,
  TAX_PREFERENCE,
  CURRENT_COUNTRY,
  VISA_PREFERENCE,
  COUNTRY_PREFERENCE,
  PASSPORT_COUNTRIES,
  ELIGIBLE_COUNTRIES,
  HOLIDAY_DESTINATION,
  HOW_MANY_PROPERTIES,
  CITIZENSHIP_PREFERENCE,
  PROPERTY_TYPE_PREFERENCE,
} = REGISTER_SURVEY_FORMS;

const RegisterForm = (props) => {
  const {
    userId = null,
    allHobbies,
    allCountries,
    allPropertyTypes,
    allDayToDayActivity,
    getAllHobbies,
    getAllCountries,
    getAllPropertyTypes,
    getAllDayToDayActivity,
    register,
    spinnerActions,
    closeModal,
    defaultValues={}
  } = props;

  const [currentSlide, setCurrentSlide] = useState(NAME_FORM);
  const [direction, setDirection] = useState("next"); // Track the slide direction
  const [allowedCountries, setAllowedCountries] = useState([]);
  const [userChoice, setUserChoice] = useState([]);

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(getRegisterSurveySchema(userId ? false : true)), // Apply the Yup schema here
    defaultValues: { isEligibleToBuy: ELIGIBILITY_OPTIONS.YES,...defaultValues },
  });

  const { reset, trigger, getValues } = methods;

  const getProgress = () => (currentSlide / (ELIGIBLE_COUNTRIES - 1)) * 100;

  const handleNext = async () => {
    const isValid = await trigger(formsNamesEnums[currentSlide]);
    if (isValid) {
      setDirection("next");
      setTimeout(() => {
        setCurrentSlide((prev) => prev + 1);
      }, 0); // Ensure direction is set first
    }
  };

  const handlePrevious = () => {
    setDirection("previous");
    setTimeout(() => {
      setCurrentSlide((prev) => prev - 1);
    }, 0); // Ensure direction is set first
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const registerSuccessFunction = (response) => {
    if (response?.needsConfirmation) {
      setAllowedCountries(response.eligibleCountries);
      setCurrentSlide(ELIGIBLE_COUNTRIES);
    } else {
      setCurrentSlide(THANK_YOU);
      reset();
      setAllowedCountries([]);
    }
  };

  const getSelectedCountries = () => {
    let selectedIds = [];
    const countriesLikeToLive = getValues("countriesLikeToLiveIds");

    if (getValues("isEligibleToBuy") === ELIGIBILITY_OPTIONS.YES) {
      selectedIds = countriesLikeToLive;
    }
    if (getValues("isEligibleToBuy") === ELIGIBILITY_OPTIONS.NO) {
      selectedIds = countriesLikeToLive.filter((id) =>
        allowedCountries.includes(id)
      );
    }

    return selectedIds;
  };

  const onSubmit = (data) => {
    const selectedCountries = getSelectedCountries();

    if (
      currentSlide === ELIGIBLE_COUNTRIES &&
      (selectedCountries === null || selectedCountries.length === 0)
    ) {
      customToast(
        "Please select at least one country you'd like to live in.",
        ERROR_TOAST
      );
      return;
    }

    const requestObj = {
      ...data,
      agentId: userId,
      ownsHouse: Number(data.ownsHouse) === 1,
      ...(currentSlide === ELIGIBLE_COUNTRIES
        ? {
            isConfirm: true,
            countriesLikeToLiveIds: selectedCountries,
          }
        : {}),
    };
    register(requestObj, registerSuccessFunction, !!userId);
  };

  useEffect(() => {
    getAllHobbies();
    getAllCountries();
    getAllPropertyTypes();
    getAllDayToDayActivity();
    // eslint-disable-next-line
  }, []);

  const handleKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent form submission
    }
  };

  useEffect(() => {
    if (currentSlide === ELIGIBLE_COUNTRIES) {
      setUserChoice(getValues("countriesLikeToLiveIds"));
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };

    // eslint-disable-next-line
  }, [currentSlide]);

  const renderSlide = () => {
    switch (currentSlide) {
      case NAME_FORM:
        return <NameForm isDisabled={!!userId} />;
      case AGE_FORM:
        return <AgeForm />;
      case OWN_HOME:
        return <OwnHome />;
      case CURRENT_COUNTRY:
        return <CurrentCountry allCountries={allCountries} />;
      case FAMILY_MEMBERS:
        return <FamilyMembers />;
      case PASSPORT_COUNTRIES:
        return <PassportCountries allCountries={allCountries} />;
      case PROPERTY_TYPE_PREFERENCE:
        return <PropetyTypePreference allPropertyTypes={allPropertyTypes} />;
      case HOW_MANY_PROPERTIES:
        return <HowManyProperties />;
      case HEALTH_CARE:
        return <HealthCare />;
      case DAY_TO_DAY:
        return <DayToDay allDayToDayActivity={allDayToDayActivity} />;
      case TAX_PREFERENCE:
        return <TaxPreference />;
      case COUNTRY_PREFERENCE:
        return <CountryPreference allCountries={allCountries} />;
      case VISA_PREFERENCE:
        return <VisaPreference />;
      case CITIZENSHIP_PREFERENCE:
        return <CitizenshipPreference allCountries={allCountries} />;
      case HOLIDAY_DESTINATION:
        return <HolidayDestinationPreference />;
      case LOVED_ONES:
        return <LovedOnesDistancePreference />;
      case HOBBIES:
        return <HobbiesAndInterests allHobbies={allHobbies} />;
      case BUDGET:
        return <BudgetRange />;
      case PASSWORD:
        return <CreatePassword isDisabled={!!userId} isEyeIcon={!!userId}/>;
      case ELIGIBLE_COUNTRIES:
        return (
          <ConfirmEligibleCountries
            allowedCountries={allowedCountries}
            userChoice={userChoice}
          />
        );
      case THANK_YOU:
        return <ThankYouScreen isAgent={!!userId} closeModal={closeModal} />;
      default:
        return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className="d-flex flex-column register-user-form"
      >
        <div
          className={`register-form-container ${
            currentSlide === THANK_YOU ? "w-100" : ""
          }`}
        >
          {currentSlide < ELIGIBLE_COUNTRIES && (
            <ProgressBar now={getProgress()} visuallyHidden />
          )}
          {currentSlide < ELIGIBLE_COUNTRIES && (
            <>
              <div className="d-flex justify-content-center survey-logo">
                <img src={LogoIcon} alt="Bayview Shores" height={44} />
                <p className="">Bayview Shores</p>
              </div>
            </>
          )}

          {spinner(authConstants.REGISTER) > -1 && (
            <div className="loader-container">
              <div className="loader" />
              <div className="survey-loading-text">
                Please wait whilst we gather the best homes for you.
              </div>
            </div>
          )}

          {spinner(authConstants.REGISTER) === -1 && (
            <SwitchTransition mode="out-in">
              <CSSTransition
                key={currentSlide}
                addEndListener={(node, done) =>
                  node.addEventListener("transitionend", done, false)
                }
                classNames={direction === "next" ? "slide-next" : "slide-prev"}
                timeout={300}
              >
                <div>{renderSlide()}</div>
              </CSSTransition>
            </SwitchTransition>
          )}
        </div>

        {spinner(authConstants.REGISTER) === -1 && (
          <div className="survey-controls">
            <div className="d-flex justify-content-between w-100 position-relative align-items-center">
              <div className="min-w-30">
                {currentSlide > NAME_FORM && currentSlide < THANK_YOU && (
                  <div
                    className="slider-button rounded-circle prev text-center pointer d-flex justify-content-center align-items-center"
                    onClick={handlePrevious}
                  >
                    <img src={ArrowLeftGreyIcon} alt="previous" />
                  </div>
                )}
              </div>
              <div>
                {(currentSlide === PASSWORD ||
                  currentSlide === ELIGIBLE_COUNTRIES) && (
                  <button
                    className="register-submit-button"
                    type="submit"
                    disabled={spinner(authConstants.REGISTER) > -1}
                  >
                    {spinner(authConstants.REGISTER) > -1
                      ? "Submitting..."
                      : "Submit"}
                  </button>
                )}
              </div>
              <div className="min-w-30">
                {currentSlide < PASSWORD && (
                  <div
                    className="slider-button rounded-circle next text-center pointer d-flex justify-content-center align-items-center"
                    onClick={handleNext}
                  >
                    <img src={ArrowRightGreyIcon} alt="next" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

const mapStateToProps = (state) => ({
  allHobbies: state.homeReducer.allHobbies,
  allCountries: state.countryReducer.allCountries,
  allPropertyTypes: state.propertyReducer.allPropertyTypes,
  spinnerActions: state.systemReducer.spinnerActions,
  allDayToDayActivity: state.homeReducer.allDayToDayActivity,
});

const mapDispatchToProps = (dispatch) => ({
  getAllHobbies: () => dispatch(homeActions.getAllHobbies()),
  getAllCountries: () => dispatch(countryAction.getAllCountries()),
  getAllPropertyTypes: () => dispatch(propertyActions.getAllPropertyTypes()),
  getAllDayToDayActivity: () => dispatch(homeActions.getAllDayToDayActivity()),
  register: (data, successFunc, isRequiredReLogin) =>
    dispatch(authActions.register(data, successFunc, isRequiredReLogin)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RegisterForm);
